import { z } from 'zod';
import { StaffLevel } from './admin-users.js';
import { MemberStoryPointRates, ReferenceAnchorMatch, StoryPointAnchor, } from './story-point-anchor.js';
import { toStoryPointScale } from './story-point-estimate.js';
/**
 * Ước lượng theo việc mẫu — APM-EREF-b, docs/plan-estimate-by-reference.md.
 *
 * Người dùng chọn **một việc cũ đã làm thật** và đánh giá việc mới tốn thời gian
 * hơn / kém bao nhiêu phần trăm. Hàm thuần ở đây dùng chung: web xem trước khi
 * kéo thanh trượt, API tính số ghi xuống — hai bên không thể ra hai con số.
 */
/**
 * Thang tỷ lệ: −75% … 0 … **×20** — người dùng yêu cầu 2026-11-02 nới bên dương từ
 * +200% ra "x1.2 hay x2.0 hay x3.5 đến tối đa là x20". Bên âm vẫn là phần trăm (dưới
 * −75% là việc khác hẳn — nên chọn mẫu khác); bên dương đọc theo **số lần** (`ratioLabel`).
 * `ratio_pct` vẫn là phần trăm cộng thêm: ×3,5 = +250, ×20 = +1900.
 */
export const REFERENCE_RATIO_MIN = -75;
export const REFERENCE_RATIO_MAX = 1900;
export const REFERENCE_RATIO_STEP = 5;
/**
 * Các nấc của thanh trượt, tăng dần. Bước mịn ở gần 0 và thưa dần khi xa — chênh
 * ×10 với ×10,5 không ai ước lượng được, chênh ×1,05 với ×1,1 thì có. Thanh trượt
 * chạy theo **chỉ số** trong danh sách này, nên các nấc trải đều trên thanh.
 */
export const REFERENCE_RATIO_VALUES = [
    ...range(-75, 100, 5), // −75% … ×2 (bước 5%)
    ...range(110, 250, 10), // ×2,1 … ×3,5
    ...range(300, 900, 50), // ×4 … ×10
    ...range(1000, 1900, 100), // ×11 … ×20
];
function range(from, to, step) {
    const out = [];
    for (let value = from; value <= to; value += step)
        out.push(value);
    return out;
}
/** Mốc chọn nhanh dưới thanh trượt: −50% · −25% · 0 · ×1,2 · ×1,5 · ×2 · ×3,5 · ×5 · ×10 · ×20. */
export const REFERENCE_RATIO_STOPS = [-50, -25, 0, 20, 50, 100, 250, 400, 900, 1900];
/** Chỉ số nấc gần nhất của một tỷ lệ — để đặt thanh trượt từ một giá trị bất kỳ. */
export function ratioIndexOf(ratioPct) {
    let best = 0;
    REFERENCE_RATIO_VALUES.forEach((value, index) => {
        if (Math.abs(value - ratioPct) < Math.abs(REFERENCE_RATIO_VALUES[best] - ratioPct)) {
            best = index;
        }
    });
    return best;
}
/**
 * Nhãn của một tỷ lệ: âm ⇒ `−25%`, 0 ⇒ `0%`, dương ⇒ số lần `×1,2` (`×` = 1 + tỷ lệ).
 * `language` để in dấu thập phân đúng ngôn ngữ giao diện (`×1,2` / `×1.2`).
 */
export function ratioLabel(ratioPct, language = 'vi') {
    if (ratioPct < 0)
        return `−${Math.abs(ratioPct)}%`;
    if (ratioPct === 0)
        return '0%';
    const factor = new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(1 + ratioPct / 100);
    return `×${factor}`;
}
/** Chênh quá ngưỡng này (cả hai chiều) ⇒ cảnh báo, và quyết định mang cờ cần PM xem lại. */
export const REFERENCE_RATIO_WARN = 50;
/** Tối đa bấy nhiêu việc mẫu khi chưa chắc chắn. */
export const MAX_REFERENCE_CANDIDATES = 3;
/** Tối đa bấy nhiêu việc ở phần "việc ≈1 SP / ≈ giờ neo của chính người làm" (`ownBaseline`). */
export const MAX_OWN_BASELINE_CANDIDATES = 5;
export const ReferenceRatio = z
    .number()
    .int()
    .min(REFERENCE_RATIO_MIN)
    .max(REFERENCE_RATIO_MAX)
    .refine((value) => value % REFERENCE_RATIO_STEP === 0, {
    message: `Tỷ lệ phải là bội của ${REFERENCE_RATIO_STEP}`,
});
/**
 * `giờ = làm tròn 0,5 (mẫu.actual × (1 + tỷ lệ))`, tối thiểu 0,5;
 * `SP = toStoryPointScale(mẫu.SP × (1 + tỷ lệ))` khi mẫu có SP.
 */
export function estimateFromReference(reference, ratioPct) {
    const factor = 1 + ratioPct / 100;
    const hours = Math.max(0.5, Math.round(reference.actualHours * factor * 2) / 2);
    const storyPoints = reference.storyPoints === null || reference.storyPoints <= 0
        ? null
        : toStoryPointScale(reference.storyPoints * factor);
    return { estimateHours: hours, storyPoints };
}
/** Tỷ lệ chênh quá ngưỡng — giao diện tô vàng, quyết định mang `needsReview`. */
export function referenceRatioWarning(ratioPct) {
    return Math.abs(ratioPct) > REFERENCE_RATIO_WARN;
}
/** Một việc mẫu cho popup: đủ để người dùng **so** — mô tả, checklist, số thật. */
export const EstimateReferenceCandidate = z.object({
    taskId: z.string(),
    key: z.string(),
    title: z.string(),
    projectName: z.string(),
    description: z.string().nullable(),
    checklist: z.array(z.string()),
    actualHours: z.number(),
    estimateHours: z.number().nullable(),
    storyPoints: z.number().int().nullable(),
    /** 0..1 — độ giống tiêu đề + mô tả. */
    similarity: z.number(),
    /**
     * Việc mốc của neo story point (APM-SPA-b) — popup đưa lên trước và ghi vì
     * sao. `null` = việc mẫu thường. Thiếu = bản API cũ.
     */
    anchor: ReferenceAnchorMatch.nullable().optional(),
    /**
     * Bậc của người đã làm việc mẫu — APM-SPA-e: popup quy giờ thật ra SP theo neo
     * khi việc chưa có SP (`convertedStoryPoints`). `null` = chưa xếp bậc / chưa giao.
     * Thiếu = bản API cũ.
     */
    doerLevel: StaffLevel.nullable().optional(),
});
export const EstimateReferenceCandidates = z.object({
    /** Mẫu đầu đủ giống và bỏ xa mẫu thứ hai ⇒ chỉ một mẫu. */
    confident: z.boolean(),
    /**
     * `similar` = tìm theo tiêu đề + mô tả; `recent` = không có việc nào tương tự nên
     * đưa các việc **đã xong gần đây** (ưu tiên cùng dự án) để vẫn có mốc mà so —
     * người dùng yêu cầu 2026-10-13. Thiếu = `similar` (bản API cũ).
     */
    source: z.enum(['similar', 'recent']).optional(),
    /**
     * Mẫu lấy ở tầng nào — người dùng yêu cầu 2026-10-14: `assignee` = việc của
     * chính người được giao, trong dự án; `project` = người khác trong dự án;
     * `org` = dự án khác. Tầng sau chỉ dùng khi tầng trước không có mẫu nào.
     * Thiếu = bản API cũ.
     */
    tier: z.enum(['assignee', 'project', 'org']).optional(),
    candidates: z.array(EstimateReferenceCandidate).max(MAX_REFERENCE_CANDIDATES),
    /**
     * Phần 1 của hộp "Ước lượng" ở drawer chi tiết việc — người dùng yêu cầu
     * 2026-11-02: việc **của chính người làm** (người được giao; chưa giao thì người
     * gọi), **trong dự án này**, đã xong, có 1 SP **hoặc** giờ thật trong khoảng giờ
     * neo (`anchorHoursRange`, ≈ 4 giờ với neo mặc định). Khác `candidates` ở chỗ
     * không đòi bậc của người làm khớp bậc neo: so với chính mình thì bậc là một.
     * Thiếu = bản API cũ.
     */
    ownBaseline: z.array(EstimateReferenceCandidate).max(MAX_OWN_BASELINE_CANDIDATES).optional(),
    /** Neo của tổ chức — popup ghi "1 SP ≈ Junior 1 làm 4 giờ". Thiếu = bản API cũ. */
    storyPointAnchor: StoryPointAnchor.optional(),
    /**
     * Giờ/SP của người được giao việc này — hộp tự tính giờ từ SP điền tay. `level` chỉ có với
     * người quản lý được giá thuê và tốc độ (2026-11-02). `null` = chưa giao. Thiếu = API cũ.
     */
    assignee: MemberStoryPointRates.nullable().optional(),
});
/**
 * `POST /tasks/:id/estimate-by-reference`. `referenceTaskId: null` = không so với
 * việc mẫu nào — hết mẫu, hoặc người dùng chọn "Tự điền số SP" — nhập giờ tay
 * (`estimateHours` bắt buộc khi ấy) và, nếu có, số SP (`storyPoints`).
 */
export const EstimateByReferenceInput = z
    .object({
    referenceTaskId: z.uuid().nullable(),
    ratioPct: ReferenceRatio,
    /**
     * Giờ ước lượng điền / sửa tay. Không mẫu: thiếu thì server tự tính từ `storyPoints`
     * theo bậc người được giao (`hoursForStoryPoints`), chưa giao ai thì để trống và tự
     * tính lúc giao (người dùng yêu cầu 2026-11-02). Có mẫu: thiếu thì theo công thức của
     * mẫu; có thì dùng số này (người chọn đã sửa).
     */
    estimateHours: z.number().positive().max(10_000).optional(),
    /**
     * SP điền thẳng (người dùng yêu cầu 2026-11-02). Không mẫu: số SP tự điền. Có mẫu:
     * số SP người chọn **sửa lại** bằng nút sửa cạnh số đã tính — thiếu thì server tính
     * từ SP của mẫu như cũ. Cùng miền với `task.storyPoints`.
     */
    storyPoints: z.number().int().min(0).max(1_000).optional(),
    /**
     * Các mẫu đã được hiện — để biết người dùng bỏ qua mẫu nào. Hộp ở drawer hiện
     * cả `ownBaseline` lẫn `candidates`, nên trần là tổng hai trần.
     */
    candidateTaskIds: z
        .array(z.uuid())
        .max(MAX_REFERENCE_CANDIDATES + MAX_OWN_BASELINE_CANDIDATES)
        .default([]),
})
    .refine((input) => input.referenceTaskId !== null ||
    input.estimateHours !== undefined ||
    input.storyPoints !== undefined, {
    message: 'Không có việc mẫu thì phải nhập số giờ hoặc số SP',
    path: ['estimateHours'],
});
/** Ngưỡng "chắc chắn": mẫu đầu giống ≥ 0,8 và hơn mẫu thứ hai ≥ 0,2. */
export function referenceConfident(similarities) {
    const [first, second] = similarities;
    if (first === undefined)
        return false;
    return first >= 0.8 && first - (second ?? 0) >= 0.2;
}
/**
 * Người chọn hay lệch thế nào — APM-EREF-d. Trên các việc **đã xong** mà chính người
 * này từng ước lượng theo mẫu: tỷ lệ họ chọn so với tỷ lệ thật
 * (`actual / reference_actual − 1`). Popup nhắc khi đủ `MIN_CALIBRATION_SAMPLES`.
 */
export const MIN_CALIBRATION_SAMPLES = 3;
export const EstimateCalibration = z.object({
    samples: z.number().int().min(0),
    /** Trung bình tỷ lệ đã chọn, %. `null` khi chưa có mẫu. */
    chosenAvgPct: z.number().nullable(),
    /** Trung bình tỷ lệ thật, %. */
    actualAvgPct: z.number().nullable(),
});
/** Trung bình hai tỷ lệ, làm tròn tới 5% như thanh trượt. */
export function calibrationOf(rows) {
    const usable = rows.filter((row) => row.referenceActualHours > 0 && row.actualHours > 0);
    if (usable.length === 0)
        return { samples: 0, chosenAvgPct: null, actualAvgPct: null };
    const round5 = (value) => Math.round(value / 5) * 5;
    const chosen = usable.reduce((sum, row) => sum + row.ratioPct, 0) / usable.length;
    const actual = usable.reduce((sum, row) => sum + (row.actualHours / row.referenceActualHours - 1) * 100, 0) /
        usable.length;
    return { samples: usable.length, chosenAvgPct: round5(chosen), actualAvgPct: round5(actual) };
}
//# sourceMappingURL=estimate-by-reference.js.map